import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import Autocomplete from 'react-google-autocomplete';
import { toast } from 'react-hot-toast';
import _omitBy from 'lodash/omitBy';

import styles from './styles.module.scss';

import { isEmail } from '@utils/helpers';
import TextInput from '@components/_shared/textInput';
import Button from '@components/_shared/button';
import { BaseComponentProps } from '@utils/types/baseComponents';
import ContactFormModal from '@components/landing/_shared/contactForm/modal';
import envVars from '@utils/config/env';
import { PhoneNumberInput } from '@components/_shared/phoneNumberInput';
import {
  postRentalProjection,
  PostRentalProjectionFields,
} from '@api/landings/postRentalProjection';
import { tr } from 'date-fns/locale';

type Props = {
  cta: string;
  formTitle?: string;
} & BaseComponentProps;

const ContactForm = (props: Props) => {
  const { formTitle, cta, containerStyle } = props;
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [fullAddress, setFullAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [reset, setReset] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pristineState, setPristineState] = useState({
    fullName: true,
    email: true,
    phoneNumber: true,
    fullAddress: true,
  });

  const resetForm = () => {
    setFullName('');
    setEmail('');
    setPhoneNumber('');
    setFullAddress('');
    setPristineState({
      fullName: true,
      email: true,
      phoneNumber: true,
      fullAddress: true,
    });
    setReset(true);
  };

  useEffect(() => {
    if (reset) {
      setReset(false);
    }
    return () => {
      resetForm();
    };
  }, [reset]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (success) {
      timeout = setTimeout(() => {
        setSuccess(false);
        resetForm();
      }, 5000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [success]);

  const onSubmit = async (data?: Partial<PostRentalProjectionFields>) => {
    setSubmitting(true);

    try {
      await postRentalProjection({
        ...(_omitBy(
          data || {},
          (value) => !value
        ) as PostRentalProjectionFields),
        email,
        phone: phoneNumber,
        full_name: fullName,
        address: fullAddress,
      });
      setSuccess(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(
        'There was an error submitting your information. Please try again.'
      );
    } finally {
      setSubmitting(false);
      setModalOpen(false);
    }
  };

  return (
    <>
      <div className={clsx([styles.contactForm__wrapper, containerStyle])}>
        {formTitle && (
          <div className={styles.contactForm__title}>{formTitle}</div>
        )}

        {success ? (
          <div className={styles.contactForm__form}>
            <svg
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0.5" width="64" height="64" rx="32" fill="#5CB3B6" />
              <path
                d="M21 33.25L29 39.75L44 24.25"
                stroke="white"
                strokeWidth="3"
              />
            </svg>

            <div className={styles.contactForm__successText}>
              Thank you for submitting your information. <br />
              We will be in touch shortly.
            </div>
          </div>
        ) : (
          <div className={styles.contactForm__form}>
            <TextInput
              label="Full Name"
              placeholder="John Doe"
              containerStyle={clsx([
                styles.textInput,
                !pristineState.fullName && !fullName && styles.withError,
              ])}
              onTextChange={setFullName}
              onBlur={() =>
                setPristineState({ ...pristineState, fullName: false })
              }
              theme="dark"
              withError={false}
              reset={reset}
            />

            <div className={styles.twoFields}>
              <TextInput
                label="Email"
                placeholder="john@doe.com"
                type="email"
                containerStyle={clsx([
                  styles.textInput,
                  !pristineState.email && !isEmail(email) && styles.withError,
                ])}
                onTextChange={setEmail}
                onBlur={() =>
                  setPristineState({ ...pristineState, email: false })
                }
                theme="dark"
                withError={false}
                reset={reset}
              />

              <PhoneNumberInput
                required
                name="phone"
                label="Phone Number"
                placeholder="888-123-4567"
                onTextChange={setPhoneNumber}
                onBlur={() =>
                  setPristineState({ ...pristineState, phoneNumber: false })
                }
                containerStyle={clsx([
                  styles.textInput,
                  !pristineState.phoneNumber &&
                    !phoneNumber &&
                    styles.withError,
                ])}
                noError
              />
            </div>

            <div className={styles.placeAutocomplete__wrapper}>
              <label
                className={styles.placeAutocomplete__label}
                htmlFor={'address'}
              >
                Property Address
              </label>
              <Autocomplete
                id={'address'}
                className={clsx([
                  styles.placeAutocomplete,
                  !pristineState.fullAddress &&
                    !fullAddress &&
                    styles.withError,
                ])}
                apiKey={envVars.googleMapsApiKey}
                onPlaceSelected={(place) => {
                  const address = place.formatted_address || '';
                  if (/^\d/.test(address)) {
                    setFullAddress(address);
                  } else {
                    // Handle the case where the address does not start with a number
                    setFullAddress('');
                    toast.error(
                      'Please select more specific address from the list.'
                    );
                  }
                }}
                options={{
                  types: ['address'],
                  componentRestrictions: { country: 'us' },
                }}
                onBlur={() =>
                  setPristineState({ ...pristineState, fullAddress: false })
                }
              />
            </div>

            <Button
              onClick={() => {
                setModalOpen(true);
              }}
              loading={submitting}
              containerStyle={styles.submitButton}
              variant="greenContained"
              disabled={
                !isEmail(email) || !fullName || !fullAddress || !phoneNumber
              }
            >
              {cta}
            </Button>
          </div>
        )}
      </div>

      <ContactFormModal
        email={email}
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          onSubmit();
        }}
        onConfirm={onSubmit}
        submitting={submitting}
      />
    </>
  );
};

export default ContactForm;
